import { MouseEvent, ReactNode, useMemo, useState } from 'react';

import { type IconButtonProps, Menu, type MenuProps } from '@mui/material';

import ActionButton from '~components/buttons/actionButton';
import ActionButtonAlignment from '~components/buttons/actionButtonAlignment';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import VectorImage from '~components/media/vectorImage';
import { StatusVariant } from '~enums';

export interface RowAction {
	key: string;
	label: string;
	onClick: () => void;
	icon?: ReactNode;
	variant?: StatusVariant;
	disabled?: boolean;
	hidden?: boolean;
}

export interface RowActionsCellProps {
	actions: RowAction[];
	buttonAriaLabel?: string;
	menuIcon?: ReactNode;
	iconButtonProps?: Partial<IconButtonProps>;
	menuProps?: Partial<MenuProps>;
}

type MenuOnCloseArgs = Parameters<NonNullable<MenuProps['onClose']>>;

const RowActionsCell = ({ actions, menuProps }: RowActionsCellProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const { onClose: externalOnClose, ...restMenuProps } = menuProps ?? {};

	const visibleActions = useMemo(
		() => actions.filter((action) => action != null && action.hidden !== true),
		[actions]
	);

	if (!visibleActions.length) {
		return null;
	}

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = (event: MenuOnCloseArgs[0], reason: MenuOnCloseArgs[1]) => {
		if (
			event &&
			typeof (event as { stopPropagation?: () => void }).stopPropagation === 'function'
		) {
			(event as { stopPropagation: () => void }).stopPropagation();
		}
		externalOnClose?.(event, reason);
		setAnchorEl(null);
	};

	return (
		<>
			<ActionButton
				startIcon={<VectorImage name='icon/dots-vertical' />}
				style={ActionButtonStyle.Text}
				onClick={handleOpen}
			/>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				{...restMenuProps}
			>
				{visibleActions.map((action) => (
					<ActionButton
						key={action.key}
						startIcon={action.icon}
						label={action.label}
						style={ActionButtonStyle.Text}
						variant={action.variant ?? StatusVariant.Neutral}
						disabled={action.disabled}
						alignment={ActionButtonAlignment.Start}
						onClick={(event) => {
							handleClose(event as MenuOnCloseArgs[0], 'backdropClick');
							if (!action.disabled) {
								action.onClick();
							}
						}}
					/>
				))}
			</Menu>
		</>
	);
};

export default RowActionsCell;
